import { useEffect, useRef, useState } from "react";
import type { useClassroom } from "./useClassroom";

type ClassroomState = ReturnType<typeof useClassroom>;
type QuestionsProps = {
  readonly classroom: ClassroomState;
  readonly questionOpen: boolean;
  readonly onClose: () => void;
};

export function ClassroomQuestions({ classroom, questionOpen, onClose }: QuestionsProps) {
  const { session, pending } = classroom;
  const [question, setQuestion] = useState("");
  const questionField = useRef<HTMLTextAreaElement>(null);
  const active = session !== null && session.status !== "complete";
  const latestAnswer = session?.answer;
  useEffect(() => {
    if (questionOpen) questionField.current?.focus();
  }, [questionOpen]);
  return (
    <>
      {(questionOpen || (latestAnswer && latestAnswer.status !== "complete")) && active && (
        <section className="panel question-panel" aria-labelledby="question-heading">
          <div className="panel-heading">
            <div>
              <span className="eyebrow">PAUSE FOR THOUGHT</span>
              <h2 id="question-heading">Ask about this lesson</h2>
            </div>
            <span className="status">Your place is saved</span>
          </div>
          <div className="panel-body stack">
            {session?.question && latestAnswer && (
              <div className="qa-answer">
                <span className="eyebrow">YOUR LATEST QUESTION</span>
                <p lang="ko">{session.question}</p>
                <span className="eyebrow">REHEARSAL RESPONSE · TEXT ONLY</span>
                <p lang="ko">{latestAnswer.text}</p>
                {latestAnswer.status !== "complete" && (
                  <button
                    type="button"
                    className="button primary"
                    disabled={pending || session.status !== "answering"}
                    onClick={() => {
                      void classroom.finishAnswer();
                      onClose();
                    }}
                  >
                    Confirm answer read &amp; resume
                  </button>
                )}
              </div>
            )}
            <form
              className="stack"
              onSubmit={(event) => {
                event.preventDefault();
                if (question.trim()) void classroom.ask(question.trim());
              }}
            >
              <label className="field">
                Your question
                <textarea
                  ref={questionField}
                  lang="ko"
                  value={question}
                  onChange={(event) => setQuestion(event.target.value)}
                  placeholder="궁금한 내용을 한국어로 입력해 주세요."
                  rows={3}
                  maxLength={4000}
                  required
                />
              </label>
              <div className="cluster">
                <button type="submit" className="button" disabled={pending || !question.trim()}>
                  Submit question
                </button>
                <span className="muted">A new question replaces the current answer.</span>
              </div>
            </form>
          </div>
        </section>
      )}
      <details className="transcript">
        <summary>
          Transcript <span>{classroom.history.length} questions</span>
        </summary>
        <div className="stack">
          {classroom.history.length === 0 ? (
            <p className="muted">
              Your questions and rehearsal responses will appear here during this session.
            </p>
          ) : (
            classroom.history.map((turn, index) => (
              <article className="transcript-turn" key={turn.id}>
                <h3>
                  Question {index + 1}
                  {index < classroom.history.length - 1 ? " · Previous turn" : " · Latest turn"}
                </h3>
                <p lang="ko">{turn.question}</p>
                <p lang="ko">{turn.answer}</p>
              </article>
            ))
          )}
          <p className="muted">This transcript stays in this page’s memory.</p>
        </div>
      </details>
    </>
  );
}
